/**
 * Formatação da área de Supervisão. Reexporta os helpers compartilhados do
 * relatório Midhaus e adiciona os formatos usados pelo protótipo (moneyK/pct/fmtVal).
 * Números sempre pt-BR; em UI use `font-mono tabular-nums`.
 */
export {
  formatInteger,
  formatPercent,
  formatTempoLongo,
  formatDuracaoCurta,
} from '@/features/relatorio/shared/format';

import { endOfMonth, format as _fmt, isValid, parseISO, startOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export type DisplayFormat = 'int' | 'money' | 'pct';

/**
 * Converte o `period` em limites de data (YYYY-MM-DD).
 * Aceita intervalo `'YYYY-MM-DD..YYYY-MM-DD'` (novo) ou mês `'YYYY-MM'` (legado).
 */
export function monthBounds(period: string): { start: string; end: string } {
  if (period.includes('..')) {
    const [start, end] = period.split('..');
    return { start, end };
  }
  const start = `${period}-01`;
  return { start, end: _fmt(endOfMonth(parseISO(start)), 'yyyy-MM-dd') };
}

/**
 * Primeiro dia do MÊS do período, como `Date`. Aceita intervalo
 * `'YYYY-MM-DD..YYYY-MM-DD'` (novo) e o legado `'YYYY-MM'`. É o ponto único de
 * derivação de data a partir do `period` para telas mensais — nunca fazer
 * `parse(`${period}-01`, …)` direto, pois um intervalo devolve Invalid Date e
 * estoura "Invalid time value" no `format()`. Robusto: cai no mês corrente.
 */
export function periodMonthStart(period: string): Date {
  const parsed = parseISO(monthBounds(period).start);
  return isValid(parsed) ? startOfMonth(parsed) : startOfMonth(new Date());
}

/** Rótulo do mês do início do período — ex.: 'jul de 2026' (para telas mensais). */
export function monthLabel(period: string): string {
  return _fmt(parseISO(monthBounds(period).start), "MMM 'de' yyyy", { locale: ptBR });
}

export const nf = (n: number | null | undefined): string =>
  n == null ? '—' : Number(n).toLocaleString('pt-BR');

export const money = (n: number | null | undefined): string =>
  n == null ? '—' : 'R$ ' + Math.round(Number(n)).toLocaleString('pt-BR');

export const moneyK = (n: number | null | undefined): string =>
  n == null ? '—' : 'R$ ' + (Number(n) / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 }) + 'k';

/** Fração (0..1) → percentual. */
export const pct = (n: number | null | undefined): string =>
  n == null ? '—' : (Number(n) * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 }) + '%';

/** Ritmo (múltiplo do esperado) → "0,74×". `null` → "—". */
export const nX = (n: number | null | undefined): string =>
  n == null ? '—' : Number(n).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '×';

export const fmtVal = (v: number | null | undefined, fmt?: DisplayFormat | string | null): string =>
  fmt === 'money' ? moneyK(v) : fmt === 'pct' ? pct(v) : nf(v);

/**
 * As RPCs `rpc_bu_metric_series*` devolvem o vocabulário do front v2
 * ('number' | 'currency' | 'percentage'), que NÃO é o `DisplayFormat` desta
 * área ('int' | 'money' | 'pct'). Sem traduzir, `fmtVal` cai no default e uma
 * métrica de dinheiro sai como número puro, sem R$ — errado e em silêncio.
 */
export const fmtDaRpc = (f?: string | null): DisplayFormat =>
  f === 'currency' ? 'money' : f === 'percentage' ? 'pct' : 'int';

/** Tempo médio na etapa (segundos vindos da RPC) → "⌀ X,Xd". */
export const dwellDays = (secs: number | null | undefined): string =>
  secs == null ? '—' : (Number(secs) / 86400).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + 'd';

/** Iniciais a partir do nome completo (fallback para e-mail). */
export const initialsOf = (name: string | null | undefined, email?: string | null): string => {
  const base = (name || email || '?').trim();
  const parts = base.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};
